import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { useCreateUserContext, ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';

import { selectTaxResidencesState } from '@/store/selectors';
import { addChildTaxResidence, addTaxResidence } from '@/features/taxResidences/store/taxResidence.actions';

import { ITaxResidenceBase } from '@/store/models/taxResidence.model';

export const usRegex = /^[a-zA-Z0-9 ]{0,50}$/;
export const italyRegex = /^[a-zA-Z]{3}[a-zA-Z]{3}[0-9]{2}[a-zA-Z][0-9]{2}[a-zA-Z0-9]{4}[a-zA-Z]$/;
export const businessItalyRegex = /^\d{11}$/;

export function useAddTaxResidences(userId?: number) {
  const { user } = useCreateUserContext();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const taxResidences = useAppSelector((state) => selectTaxResidencesState(state, userId)?.create);

  const isTaxResidenceCreated =
    (user?.taxResidences || []).filter((taxResidence) => taxResidence.country).length > 0
      ? taxResidences?.state && Object.values(taxResidences.state).every((state) => state.isLoading === false)
      : true;

  const isTaxResidenceLoading = Object.values(taxResidences?.state ?? {}).some((state) => state.isLoading === true);

  const onAddTaxResidence = useCallback(
    (taxResidence: ITaxResidenceBase, index: number) => dispatch(addTaxResidence(taxResidence, `batch-${index}`, true)),
    [dispatch],
  );

  const addTaxResidences = useCallback(
    (taxResidences: ITaxResidenceCreate[], userId: number) => {
      (taxResidences || [])
        .filter((taxResidence) => taxResidence.country)
        .map(({ isDirty, ...taxResidence }, index) => {
          const taxResidenceToAdd = { ...taxResidence, userId };
          onAddTaxResidence(taxResidenceToAdd, index);
        });
    },
    [onAddTaxResidence],
  );

  const onAddChildTaxResidence = useCallback(
    (parentId: number, taxResidence: ITaxResidenceBase, index: number) =>
      dispatch(addChildTaxResidence(parentId, taxResidence, `batch-${index}`, true)),
    [dispatch],
  );

  const addChildTaxResidences = useCallback(
    (taxResidences: ITaxResidenceCreate[], userId: number, parentUserId: number) => {
      (taxResidences || [])
        .filter((taxResidence) => taxResidence.country)
        .map(({ isDirty, ...taxResidence }, index) => {
          const taxResidenceToAdd = { ...taxResidence, userId };
          onAddChildTaxResidence(parentUserId, taxResidenceToAdd, index);
        });
    },
    [onAddChildTaxResidence],
  );

  const isTaxResidenceDisabled = useCallback(
    (taxResidences?: ITaxResidenceCreate[], isBusiness?: boolean) =>
      taxResidences?.length > 0 &&
      taxResidences.some((taxResidence: ITaxResidenceCreate, index: number) => {
        if (taxResidence.country === 'US' && !taxResidence.taxPayerId) return true;
        if (
          taxResidence.country === 'IT' &&
          !(isBusiness ? businessItalyRegex : italyRegex).test(taxResidence.taxPayerId)
        )
          return true;
        if (
          taxResidences.slice(index + 1).some((item) => item.country !== null && item.country === taxResidence.country)
        ) {
          return true;
        }
        return false;
      }),
    [],
  );

  const getInputError = useCallback(
    (taxResidence, isBusiness?: boolean) => {
      if (taxResidence.taxPayerId) {
        if (taxResidence.country === 'US' && !usRegex.test(taxResidence.taxPayerId))
          return t('pages.currentProfiles.profile.taxResidence.form.rules.pattern', { maxLength: 50 });
        if (
          taxResidence.country === 'IT' &&
          !(isBusiness ? businessItalyRegex : italyRegex).test(taxResidence.taxPayerId)
        )
          return t('pages.currentProfiles.profile.taxResidence.form.rules.format');
      }
      return undefined;
    },
    [t],
  );

  return {
    onAddTaxResidence,
    addTaxResidences,
    addChildTaxResidences,
    isTaxResidenceCreated,
    isTaxResidenceDisabled,
    isTaxResidenceLoading,
    getInputError,
  };
}
